import { isDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import { Toast } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCurrentUserQuery } from '@/api/user/index.ts';
import { LegDetails } from '@/App/Growl/products/option/LegDetails.tsx';
import { GrowlError } from '@/App/Growl/shared/GrowlError.tsx';
import { GrowlItem } from '@/App/Growl/shared/GrowlItem.tsx';
import { getExecutionColorAndIcon } from '@/App/Growl/utilities.ts';
import { formatStrikeForDisplay } from '@/App/tiles/Option/utils/strikeUtils.ts';
import { FormattedFXDate } from '@/components/ui/FormattedFXDate.tsx';
import { Panel } from '@/components/ui/Panel/index.tsx';
import type { VanillaExecution } from '@/store/growl/state/option.ts';

export type GrowlOptionVanillaProps = {
  growlItem: VanillaExecution;
};

export const GrowlOptionVanilla = (props: GrowlOptionVanillaProps) => {
  const { growlItem } = props;
  const { formatNumber } = useIntl();

  const { data: currentUser } = useCurrentUserQuery();

  const { tradeDate, accountId, optionCurrency, way, productType, quoteCcy, premiumCurrency, legs, premiumType } =
    growlItem.data;

  const singleLeg = legs.length === 1 ? legs[0] : undefined;
  const isNamedStrategy = productType === 'Strangle' || productType === 'Straddle' || productType === 'RiskReversal';
  const summaryStrike = isNamedStrategy ? legs[0]?.strike : singleLeg?.strike;

  const { currencyPair } = optionCurrency;
  const accountName = isDefined(currentUser)
    ? currentUser.clients.find((client) => String(client.companyId) === accountId)?.companyName
    : undefined;

  const [color, icon] = getExecutionColorAndIcon(growlItem.status);
  const isInternalSales = currentUser?.userType === 'Internal Sales';

  const productName = isNamedStrategy ? productType : singleLeg?.productName ?? 'Strategy';

  const signedPremiumAmount = (way === 'Ask' ? quoteCcy?.askAmount : quoteCcy?.bidAmount) ?? 0;
  const premiumWay =
    way === 'Bid' ? (signedPremiumAmount >= 0 ? 'Receive' : 'Pay') : signedPremiumAmount >= 0 ? 'Pay' : 'Receive';

  return (
    <>
      <Toast.Header className={`bg-${color} text-${color} bg-opacity-10`} data-e2e="growl-header">
        <i className="icon icon-sm me-2">{icon}</i>
        <strong className="me-auto">
          <FormattedMessage id={`growl.status.${growlItem.status}`} defaultMessage={growlItem.status || 'Unknown'} />
        </strong>
      </Toast.Header>

      <Toast.Body>
        <span data-e2e="growl-message">
          <FormattedMessage
            id={`growl.option.${productName}.${way}`}
            values={{
              currencyPair,
              isInternalSales,
              side: singleLeg?.type,
              rate: formatStrikeForDisplay(summaryStrike, { maximumFractionDigits: 10 }),
              rate2: formatStrikeForDisplay(isNamedStrategy ? legs[1]?.strike : undefined, { maximumFractionDigits: 10 }),
            }}
          />
        </span>

        {growlItem.status === 'rejected' && (
          <GrowlError
            errorCode={growlItem.errorCode}
            multipassErrorCode={growlItem.multipassErrorCode}
            reason={growlItem.reason}
            sessionId={growlItem.sessionId}
          />
        )}

        <Panel labelId={'growl.option.deals.details'} data-e2e="growl-details-toggle-button">
          <HStack horizontalAlignment="full" className="border-bottom py-8px fs-12">
            <span className="text-secondary" data-e2e="growl-client-way">
              <FormattedMessage id={`growl.option.action.${premiumWay}`} values={{ isInternalSales }} />
            </span>

            <span className="text-truncate text-end" data-e2e="premium-message">
              {premiumCurrency} {formatNumber(Math.abs(signedPremiumAmount), { minimumFractionDigits: 0 })}
            </span>
          </HStack>

          {/*<GrowlItem*/}
          {/*  labelId={`growl.option.action.${premiumWay}`}*/}
          {/*  labelValues={{ isInternalSales }}*/}
          {/*  data-e2e="growl-client-way"*/}
          {/*>*/}
          {/*  <FormattedNumber*/}
          {/*    value={Math.abs(signedPremiumAmount)}*/}
          {/*    minimumFractionDigits={0}*/}
          {/*    style="currency"*/}
          {/*    currency={premiumCurrency}*/}
          {/*    currencyDisplay="code"*/}
          {/*  />*/}
          {/*</GrowlItem>*/}

          <GrowlItem labelId="growl.account" data-e2e="growl-account">
            {accountName}
          </GrowlItem>

          <GrowlItem labelId="growl.tradeDate" data-e2e="growl-tradeDate">
            <FormattedFXDate value={tradeDate} dateStyle="medium" />
          </GrowlItem>

          <GrowlItem labelId="growl.tradeId" data-e2e="growl-tradeId">
            {growlItem.status === 'confirmed' ? (
              growlItem.tradeIds.join(', ')
            ) : (
              <FormattedMessage id="growl.tradeId.awaiting" />
            )}
          </GrowlItem>

          <LegDetails legs={legs} way={way} premiumType={premiumType} optionCurrency={optionCurrency} />
        </Panel>
      </Toast.Body>
    </>
  );
};
